import { memo, useEffect, useState } from "react";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";

import { useLoaderScreenVariants } from "@/utils/providers/LoaderScreenVariantsProvider";

interface ProgressProps {
    onLoaded: () => void;
    onHalfLoaded?: () => void;
    className?: string;
    runProgress?: boolean;
}

const pausePercentages = {
    30: 30,
    50: 50,
    69: 69,
    100: 100,
};

const Progress = ({ runProgress = true, onLoaded, onHalfLoaded, className }: ProgressProps) => {
    const [progress, setProgress] = useState(0);
    const { styles, isLoaderScreenTest } = useLoaderScreenVariants();

    useEffect(() => {
        if (runProgress) {
            const interval = setInterval(() => {
                setProgress((prevState) => {
                    if (prevState >= pausePercentages["100"]) {
                        return prevState;
                    }

                    if (
                        prevState === pausePercentages["30"] ||
                        prevState === pausePercentages["69"]
                    ) {
                        return prevState;
                    }

                    const result = prevState + 3;

                    return result >= pausePercentages["100"] ? pausePercentages["100"] : result;
                });
            }, 100);
            return () => clearInterval(interval);
        }
    }, [runProgress]);

    useEffect(() => {
        if (progress === pausePercentages["30"] || progress === pausePercentages["69"]) {
            const timeOut = setTimeout(() => {
                setProgress((prevState) => prevState + 3);
                return () => clearTimeout(timeOut);
            }, 700);
        }
        if (progress >= pausePercentages["50"]) {
            onHalfLoaded && onHalfLoaded();
        }
        if (progress >= pausePercentages["100"]) {
            onLoaded();
        }
        // eslint-disable-next-line
    }, [progress]);

    return (
        <div
            className={twMerge(
                classNames(
                    "relative flex h-8 w-full flex-col rounded-2xl bg-zing-neutral-cool-grey-800 p-1 female:bg-zing-neutral-cool-grey-200 female:bg-opacity-[0.24]",
                    {
                        "female:!bg-opacity-1 female:!bg-white":
                            styles?.textColor === "text-black" && isLoaderScreenTest,
                    }
                ),
                className
            )}
        >
            <div
                className="h-6 rounded-2xl bg-zing-cool-blue-500 female:bg-zing-warm-tangerine-500"
                style={{ width: `${progress}%` }}
            ></div>
            <div
                className={twMerge(
                    "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-semibold text-white lg:text-zing-24-32",
                    styles?.textColor
                )}
            >
                {progress}%
            </div>
        </div>
    );
};

export default memo(Progress);
